import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';

import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// PrimeNG Imports
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DividerModule } from 'primeng/divider';

// Services
import { MeasurementsService, AnalyticsService } from '@cosmos/angular/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SHM_VARIABLES } from '@cosmos/shared';

// Chart.js
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import 'chartjs-adapter-moment';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES AND INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface QueryableFieldConfig {
  key: string;
  path: string;
  label: string;
  type: 'categorical' | 'numeric' | 'date';
  role: 'groupBy' | 'metric' | 'filter';
  filterable?: boolean;
  filterType?: 'categorical' | 'numeric';
  measurementTypes?: string[];
}

interface FilterState {
  availableValues: Array<{ value: string | number; count: number }>;
  loading: boolean;
}

interface DatasetResponse {
  datasetLabel: string;
  dataPoints: Array<{ x: string | Date; y: number; count?: number }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// QUERYABLE FIELDS CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════
const QUERYABLE_FIELDS: QueryableFieldConfig[] = [
  { key: 'name', path: 'name', label: 'name', type: 'categorical', role: 'groupBy', filterable: true, filterType: 'categorical' },
  { key: 'device.name', path: 'device.name', label: 'deviceName', type: 'categorical', role: 'groupBy', filterable: true, filterType: 'categorical' },
  { key: 'active', path: 'active', label: 'activeStatus', type: 'categorical', role: 'groupBy', filterable: true, filterType: 'categorical' },
  { key: 'channel', path: 'channel', label: 'channel', type: 'categorical', role: 'groupBy', measurementTypes: ['StructuralHealthMeasurement'] },
  { key: 'temperature', path: 'temperature', label: 'temperature', type: 'numeric', role: 'metric', measurementTypes: ['MaturityMeasurement', 'TemperatureMeasurement', 'StructuralHealthMeasurement'] },
  { key: 'maturity', path: 'maturity', label: 'maturity', type: 'numeric', role: 'metric', measurementTypes: ['MaturityMeasurement'] },
  { key: 'strength', path: 'strength', label: 'strength', type: 'numeric', role: 'metric', measurementTypes: ['MaturityMeasurement'] },
  ...Object.keys(SHM_VARIABLES.corrosion).map(v => ({
    key: v, path: v,
    label: SHM_VARIABLES.corrosion[v as keyof typeof SHM_VARIABLES.corrosion].label,
    type: 'numeric' as const, role: 'metric' as const,
    measurementTypes: ['StructuralHealthMeasurement']
  })),
  ...Object.keys(SHM_VARIABLES.ph).map(v => ({
    key: v, path: v,
    label: SHM_VARIABLES.ph[v].label,
    type: 'numeric' as const, role: 'metric' as const,
    measurementTypes: ['StructuralHealthMeasurement']
  })),
  ...Object.keys(SHM_VARIABLES.chlorides).map(v => ({
    key: v, path: v,
    label: SHM_VARIABLES.chlorides[v].label,
    type: 'numeric' as const, role: 'metric' as const,
    measurementTypes: ['StructuralHealthMeasurement']
  })),
  { key: 'createdAt', path: 'createdAt', label: 'createdDate', type: 'date', role: 'filter' }
];

const TIME_AGGREGATIONS = [
  { key: 'none', label: 'none' },
  { key: 'day', label: 'day' },
  { key: 'week', label: 'week' },
  { key: 'month', label: 'month' },
  { key: 'quarter', label: 'quarter' },
  { key: 'group', label: 'group' }
];

const AGGREGATION_FUNCTIONS = [
  { key: 'average', label: 'average' },
  { key: 'sum', label: 'sum' },
  { key: 'min', label: 'min' },
  { key: 'max', label: 'max' },
  { key: 'count', label: 'count' }
];

const NUMERIC_OPERATORS = [
  { key: 'gt', label: 'gt' }, { key: 'gte', label: 'gte' },
  { key: 'lt', label: 'lt' }, { key: 'lte', label: 'lte' },
  { key: 'eq', label: 'eq' }, { key: 'neq', label: 'neq' }
];

const FILTER_LOGIC_OPTIONS = [
  { key: 'and', label: 'and' },
  { key: 'or', label: 'or' }
];

@Component({
  selector: 'cosmos-monitoring-data-explorer',
  imports: [
    FormsModule, ReactiveFormsModule, CardModule, SelectModule, DatePickerModule,
    ButtonModule, ProgressSpinnerModule, MessageModule, TranslateModule,
    IftaLabelModule, DividerModule, MultiSelectModule, InputNumberModule, SelectButtonModule
  ],
  templateUrl: './monitoring-data-explorer.component.html',
  styleUrls: ['./monitoring-data-explorer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonitoringDataExplorerComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly measurementsService = inject(MeasurementsService);
  private readonly analyticsService = inject(AnalyticsService);
  readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly chartCanvas = viewChild<ElementRef<HTMLCanvasElement>>('chartCanvas');

  readonly loading = signal(false);
  readonly hasData = signal(false);
  readonly errorMessage = signal('');
  readonly showAggregationFunction = signal(false);

  explorerForm!: FormGroup;
  readonly projectId: string;

  readonly groupByFields: QueryableFieldConfig[];
  readonly metricFields: QueryableFieldConfig[];
  filterableFields: QueryableFieldConfig[];
  readonly timeAggregations: Array<{ key: string; label: string }>;
  readonly aggregationFunctions: Array<{ key: string; label: string }>;
  readonly numericOperators: Array<{ key: string; label: string }>;
  readonly filterLogicOptions: Array<{ key: string; label: string }>;

  availableMeasurementTypes: string[] = [];
  filteredMetricFields: QueryableFieldConfig[] = [];
  filteredGroupByFields: QueryableFieldConfig[] = [];

  filterStates: FilterState[] = [];
  readonly maxFilters = 2;

  private chart: Chart | null = null;

  constructor() {
    Chart.register(...registerables);

    this.projectId = this.route.snapshot.params['project'];

    this.groupByFields = QUERYABLE_FIELDS
      .filter(f => f.role === 'groupBy')
      .map(f => ({ ...f, label: this.translateService.instant('monitoring.labels.' + f.label) }));

    this.metricFields = QUERYABLE_FIELDS
      .filter(f => f.role === 'metric')
      .map(f => ({ ...f, label: this.translateService.instant('monitoring.labels.' + f.label) }));

    this.filterableFields = QUERYABLE_FIELDS
      .filter(f => f.filterable)
      .map(f => ({ ...f, label: this.translateService.instant('monitoring.labels.' + f.label) }));

    this.timeAggregations = TIME_AGGREGATIONS.map(t => ({
      ...t, label: this.translateService.instant('analytics.labels.aggregation.' + t.label)
    }));

    this.aggregationFunctions = AGGREGATION_FUNCTIONS.map(a => ({
      ...a, label: this.translateService.instant('analytics.labels.aggregation.functions.' + a.label)
    }));

    this.numericOperators = NUMERIC_OPERATORS.map(o => ({
      ...o, label: this.translateService.instant('analytics.labels.operators.' + o.label)
    }));

    this.filterLogicOptions = FILTER_LOGIC_OPTIONS.map(f => ({
      ...f, label: this.translateService.instant('analytics.labels.logic.' + f.label)
    }));

    this.updateFilteredMetrics(null);
    this.initForm();
    this.setupFormListeners();
    this.fetchAvailableTypes();

    this.destroyRef.onDestroy(() => this.destroyChart());
  }

  private fetchAvailableTypes() {
    this.measurementsService.getAvailableMeasurementTypes(this.projectId).subscribe({
      next: (response) => {
        this.availableMeasurementTypes = response.body || [];
      },
      error: (e) => console.error('Error fetching measurement types', e)
    });
  }

  private initForm() {
    this.explorerForm = this.fb.group({
      measurementType: [null, [Validators.required]],
      dateRange: [null],
      metricField: [{ value: null, disabled: true }, [Validators.required]],
      groupByField: [null],
      groupByField2: [null],
      timeAggregation: [this.timeAggregations[0]],
      aggregation: [this.aggregationFunctions[0]],
      filterLogic: ['and'],
      filters: this.fb.array([])
    });
  }

  get filtersArray(): FormArray {
    return this.explorerForm.get('filters') as FormArray;
  }

  private setupFormListeners() {
    this.explorerForm.get('measurementType')?.valueChanges.subscribe((type) => {
      this.updateFilteredMetrics(type);
      this.explorerForm.get('metricField')?.setValue(null);
      this.explorerForm.get('groupByField2')?.setValue(null);

      if (type) {
        this.explorerForm.get('metricField')?.enable();
        if (type === 'StructuralHealthMeasurement') {
          const channelGroup = this.filteredGroupByFields.find(f => f.key === 'channel');
          if (channelGroup) {
            this.explorerForm.get('groupByField')?.setValue(channelGroup);
          }
        } else {
          this.explorerForm.get('groupByField')?.setValue(null);
        }
      } else {
        this.explorerForm.get('metricField')?.disable();
        this.explorerForm.get('groupByField')?.setValue(null);
      }
      this.clearAllFilters();
    });

    this.explorerForm.get('groupByField')?.valueChanges.subscribe(() => {
      this.explorerForm.get('groupByField2')?.setValue(null);
    });

    this.explorerForm.get('metricField')?.valueChanges.subscribe(() => {
      this.clearAllFilters();
    });

    this.explorerForm.get('timeAggregation')?.valueChanges.subscribe((timeAgg) => {
      this.showAggregationFunction.set(timeAgg?.key !== 'none');
    });
  }

  private updateFilteredMetrics(type: string | null) {
    this.filteredMetricFields = QUERYABLE_FIELDS
      .filter(f => f.role === 'metric' && (!f.measurementTypes || (type && f.measurementTypes.includes(type))))
      .map(f => ({ ...f, label: this.translateService.instant('monitoring.labels.' + f.label) }));

    this.filteredGroupByFields = QUERYABLE_FIELDS
      .filter(f => f.role === 'groupBy' && (!f.measurementTypes || (type && f.measurementTypes.includes(type))))
      .map(f => ({ ...f, label: this.translateService.instant('monitoring.labels.' + f.label) }));

    this.filterableFields = QUERYABLE_FIELDS
      .filter(f => f.filterable && (!f.measurementTypes || (type && f.measurementTypes.includes(type))))
      .map(f => ({ ...f, label: this.translateService.instant('monitoring.labels.' + f.label) }));
  }

  canAddFilter(): boolean {
    return this.filtersArray.length < this.maxFilters;
  }

  getAvailableFilterFields(currentIndex: number): QueryableFieldConfig[] {
    const usedKeys: string[] = [];
    this.filtersArray.controls.forEach((control, idx) => {
      if (idx !== currentIndex) {
        const fieldValue = control.get('field')?.value;
        if (fieldValue?.key) usedKeys.push(fieldValue.key);
      }
    });
    return this.filterableFields.filter(f => !usedKeys.includes(f.key));
  }

  getAvailableGroupBy2Fields(): QueryableFieldConfig[] {
    const mainGroupBy = this.explorerForm.get('groupByField')?.value;
    if (!mainGroupBy) return [];
    return this.filteredGroupByFields.filter(f => f.key !== mainGroupBy.key);
  }

  addFilter() {
    if (!this.canAddFilter()) return;
    const filterGroup = this.fb.group({
      field: [null],
      selectedValues: [[]],
      operator: ['eq'],
      numericValue: [null]
    });
    this.filtersArray.push(filterGroup);
    this.filterStates.push({ availableValues: [], loading: false });
  }

  onFilterFieldChange(index: number) {
    const filterGroup = this.filtersArray.at(index) as FormGroup;
    const field = filterGroup.get('field')?.value as QueryableFieldConfig;
    if (!field) return;

    filterGroup.patchValue({ selectedValues: [], operator: 'eq', numericValue: null });

    if (field.filterType === 'categorical') {
      this.filterStates[index].loading = true;
      const measurementType = this.explorerForm.get('measurementType')?.value;
      this.measurementsService.getDistinctValues(this.projectId, field.path, measurementType).subscribe({
        next: (values: any) => {
          this.filterStates[index].availableValues = values.body || [];
          this.filterStates[index].loading = false;
        },
        error: () => this.filterStates[index].loading = false
      });
    }
  }

  removeFilter(index: number) {
    this.filtersArray.removeAt(index);
    this.filterStates.splice(index, 1);
  }

  clearAllFilters() {
    while (this.filtersArray.length > 0) this.filtersArray.removeAt(0);
    this.filterStates = [];
  }

  getFilterField(index: number): QueryableFieldConfig | null {
    return this.filtersArray.at(index)?.get('field')?.value || null;
  }

  onApply() {
    this.fetchData();
  }

  private buildParams() {
    const formValue = this.explorerForm.value;
    const metricConfig = formValue.metricField as QueryableFieldConfig;
    const groupByConfig = formValue.groupByField as QueryableFieldConfig | null;
    const groupByConfig2 = formValue.groupByField2 as QueryableFieldConfig | null;
    const timeAgg = formValue.timeAggregation?.key || 'none';

    const params: any = {
      metricField: metricConfig.path,
      timeAggregation: timeAgg,
    };

    if (formValue.measurementType) {
      params.measurementType = formValue.measurementType;
    }

    if (formValue.dateRange && formValue.dateRange[0] && formValue.dateRange[1]) {
      params.dateRange = { start: formValue.dateRange[0], end: formValue.dateRange[1] };
    }

    if (groupByConfig) {
      params.groupByField = groupByConfig.path;
      if (groupByConfig2) {
        params.groupByField2 = groupByConfig2.path;
      }
    }

    if (timeAgg !== 'none') {
      params.aggregation = formValue.aggregation?.key || 'average';
    }

    const validFilters = formValue.filters.filter((f: any) => {
      if (!f.field) return false;
      return f.field.filterType === 'categorical' ? (f.selectedValues?.length > 0) : (f.operator && f.numericValue != null);
    });

    if (validFilters.length > 0) {
      params.filters = validFilters.map((f: any) => ({
        field: f.field.path,
        filterType: f.field.filterType!,
        values: f.field.filterType === 'categorical' ? f.selectedValues : undefined,
        operator: f.field.filterType === 'numeric' ? f.operator : undefined,
        numericValue: f.field.filterType === 'numeric' ? f.numericValue : undefined
      }));
      params.filterLogic = formValue.filterLogic;
    }

    return params;
  }

  private fetchData() {
    this.loading.set(true);
    this.errorMessage.set('');
    const params = this.buildParams();
    const timeAgg = this.explorerForm.value.timeAggregation?.key || 'none';

    this.measurementsService.exploreMeasurements(this.projectId, params).subscribe({
      next: (response: any) => {
        this.loading.set(false);
        if (response?.body?.length > 0) {
          this.hasData.set(true);
          this.updateChart(response.body as DatasetResponse[], timeAgg);
        } else {
          this.hasData.set(false);
          this.destroyChart();
        }
      },
      error: (error) => {
        this.loading.set(false);
        this.hasData.set(false);
        this.errorMessage.set('monitoring.data-explorer.error-loading');
        console.error('Monitoring explorer error:', error);
      }
    });
  }

  private updateChart(datasets: DatasetResponse[], timeAgg: string) {
    this.destroyChart();
    const canvasEl = this.chartCanvas()?.nativeElement;
    if (!canvasEl) return;

    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;

    const formValue = this.explorerForm.value;
    const metricConfig = formValue.metricField as QueryableFieldConfig;

    let config: ChartConfiguration;
    if (timeAgg === 'group') {
      config = this.buildBarChartConfig(datasets, metricConfig);
    } else if (timeAgg === 'none') {
      config = this.buildScatterChartConfig(datasets, metricConfig);
    } else {
      config = this.buildLineChartConfig(datasets, metricConfig, timeAgg);
    }

    this.chart = new Chart(ctx, config);
  }

  private buildBarChartConfig(datasets: DatasetResponse[], metricConfig: QueryableFieldConfig): ChartConfiguration {
    return {
      type: 'bar',
      data: {
        labels: datasets.map(ds => ds.datasetLabel),
        datasets: [{
          label: metricConfig.label,
          data: datasets.map(ds => ds.dataPoints[0]?.y || 0),
          borderWidth: 1
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { title: { display: true, text: 'Group' } },
          y: { title: { display: true, text: metricConfig.label }, beginAtZero: true }
        }
      }
    };
  }

  private buildScatterChartConfig(datasets: DatasetResponse[], metricConfig: QueryableFieldConfig): ChartConfiguration {
    return {
      type: 'scatter',
      data: {
        datasets: datasets.map(ds => ({
          label: ds.datasetLabel,
          data: ds.dataPoints.map(dp => ({ x: new Date(dp.x).getTime(), y: dp.y })),
          borderWidth: 1
        }))
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: datasets.length > 1, position: 'top' },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const date = new Date(context.parsed.x);
                return `${context.dataset.label}: ${context.parsed.y} (${date.toLocaleDateString()})`;
              }
            }
          }
        },
        scales: {
          x: { type: 'time', time: { unit: 'day' }, title: { display: true, text: 'Date' } },
          y: { title: { display: true, text: metricConfig.label }, beginAtZero: true }
        }
      }
    };
  }

  private buildLineChartConfig(datasets: DatasetResponse[], metricConfig: QueryableFieldConfig, timeAgg: string): ChartConfiguration {
    const allLabels = Array.from(new Set(datasets.flatMap(ds => ds.dataPoints.map(dp => dp.x as string)))).sort();
    return {
      type: 'line',
      data: {
        labels: allLabels,
        datasets: datasets.map(ds => {
          const dataMap = new Map(ds.dataPoints.map(dp => [dp.x as string, dp.y]));
          return {
            label: ds.datasetLabel,
            data: allLabels.map(l => dataMap.get(l) ?? null),
            borderWidth: 2, tension: 0.3, fill: false
          };
        })
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: datasets.length > 1, position: 'top' } },
        scales: {
          x: { title: { display: true, text: `Time (${timeAgg})` } },
          y: { title: { display: true, text: metricConfig.label }, beginAtZero: true }
        }
      }
    };
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
